import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Play } from 'lucide-react';
import { formatPrice, extractInstructor } from '../lib/utils';

export const VideoCard = ({ video, type = 'video' }) => {
    const instructor = video.instructor || extractInstructor(video.title);
    const linkTo = type === 'course' 
        ? `/courses/${video.course_id || video.id}` 
        : `/video/${video.id}`;

    // Default thumbnail based on type
    const defaultThumbnail = type === 'course'
        ? 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=400'
        : 'https://images.unsplash.com/photo-1579783902614-a3fb3927b6a5?crop=entropy&cs=srgb&fm=jpg&q=85&w=400';

    const [imgSrc, setImgSrc] = useState(video.thumbnail || defaultThumbnail);
    const [imgError, setImgError] = useState(false);

    const handleImageError = () => {
        if (!imgError) {
            setImgError(true);
            setImgSrc(defaultThumbnail);
        }
    };

    return (
        <Link to={linkTo} data-testid={`video-card-${video.id}`}>
            <Card className="group bg-card border border-border/60 hover:border-accent/30 transition-all duration-300 overflow-hidden card-hover">
                {/* Thumbnail */}
                <div className="relative aspect-video overflow-hidden bg-muted">
                    <img
                        src={imgSrc}
                        alt={video.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={handleImageError}
                    />
                    {/* Play overlay */}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full bg-accent flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 shadow-lg">
                            <Play className="h-6 w-6 text-accent-foreground ml-1" fill="currentColor" />
                        </div>
                    </div>
                    {/* Type badge */}
                    <div className="absolute top-3 left-3">
                        <span className="px-2 py-1 text-xs font-medium uppercase tracking-wider bg-background/90 text-foreground rounded">
                            {type === 'course' ? 'Course' : 'Video'}
                        </span>
                    </div>
                </div>

                <CardContent className="p-4">
                    {/* Title */}
                    <h3 className="font-serif text-lg font-medium text-foreground line-clamp-2 group-hover:text-accent transition-colors">
                        {video.title}
                    </h3>

                    {/* Instructor */}
                    {instructor && (
                        <p className="text-sm text-muted-foreground mt-1">
                            with {instructor}
                        </p>
                    )}

                    {/* Price */}
                    {video.cost > 0 && (
                        <p className="text-sm font-semibold text-foreground mt-3">
                            {formatPrice(video.cost)}
                        </p>
                    )}

                    {/* Lesson count for courses */}
                    {type === 'course' && video.lessons?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-2">
                            {video.lessons.length} lessons
                        </p>
                    )}
                </CardContent>
            </Card>
        </Link>
    );
};

export default VideoCard;
